// StripePopup.jsx
import { Button } from "@/components/ui/button";
import {
  CardElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useEffect, useState, useRef } from "react";
import { FiLoader, FiX } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { LockIcon } from "lucide-react";
import { apiEndpoints } from "../../services/apiConfig";

// Use test or live key based on environment
const API_BASE = import.meta.env.VITE_BACKEND_PROD_URL;
const stripePromise = loadStripe(import.meta.env.VITE_BACKEND_STRIPE_LIVE_PK);

// const API_BASE = import.meta.env.VITE_BACKEND_LOCAL_URL;
// const stripePromise = loadStripe(import.meta.env.VITE_BACKEND_STRIPE_TEST_PK);

function StripeCheckout({
  isOpen,
  onClose,
  batchId,
  count,
  totalAmount,
  originalAmount,
  couponCode = null,
  userDetails,
  onPaymentSuccess,
  remainingSessionId = "",
  remainingDownload = false,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const [clientSecret, setClientSecret] = useState(null);
  const [jobId, setJobId] = useState(null); // Add state for job_id
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);

  // Add ref for timeout
  const initTimeoutRef = useRef(null);

  // Check if JWT token is available
  const JWT = localStorage.getItem("token") || "";

  // Initialize payment when component opens
  useEffect(() => {
    if (isOpen && batchId && count && userDetails?.email) {
      // Clear previous timeout
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
      }

      // Set new timeout for throttling
      initTimeoutRef.current = setTimeout(() => {
        createPaymentIntent();
      }, 500);
    }

    return () => {
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
      }
    };
  }, [isOpen, batchId, count, userDetails?.email]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  // Function to create payment intent
  const createPaymentIntent = async () => {
    setMsg(null);
    setClientSecret(null);
    setJobId(null); // Reset job_id
    setIsCreatingOrder(true);

    const sessionId = localStorage.getItem("sessionId");

    const payload = {
      session_id: !remainingDownload ? sessionId : remainingSessionId,
      batch_id: batchId,
      count: count,
      currency: "USD",
    };

    if (couponCode) {
      payload.coupon_code = couponCode;
    }

    try {
      const apiUrl = apiEndpoints.createEmailDownloadOrder;

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${JWT}`,
          "Content-Type": "application/json",
          "Idempotency-Key":
            (typeof self !== "undefined" &&
              self.crypto &&
              typeof self.crypto.randomUUID === "function" &&
              self.crypto.randomUUID()) ||
            String(Date.now()),
        },
        body: JSON.stringify(payload),
      }).catch((error) => {
        console.error("Fetch error:", error);
        throw error;
      });

      const res = await response.json();

      if (!res?.success) {
        setMsg(res?.message || res?.data?.error || "Could not create payment");
        setIsCreatingOrder(false);
        return;
      }

      // Store the job_id from response
      if (res.data.job_id) {
        setJobId(res.data.job_id);
      } else {
        setMsg("Missing job_id in response");
        setIsCreatingOrder(false);
        return;
      }

      // For Stripe, we need to get client secret from the response
      if (res.data.client_secret) {
        setClientSecret(res.data.client_secret);
      } else if (
        res.data.payment_intent &&
        res.data.payment_intent.client_secret
      ) {
        setClientSecret(res.data.payment_intent.client_secret);
      } else {
        setMsg("Invalid payment response from server");
        setIsCreatingOrder(false);
        return;
      }
    } catch (e) {
      console.error("Error creating payment intent:", e);
      setMsg(`Error creating payment intent: ${e.message || "Unknown error"}`);
    } finally {
      setIsCreatingOrder(false);
    }
  };

  // Confirm payment with Stripe
  const handlePayment = async () => {
    if (!stripe || !elements || !clientSecret) {
      setMsg("Payment system not ready. Please try again.");
      return;
    }

    setLoading(true);
    setMsg(null);

    const cardElement = elements.getElement(CardElement);

    if (!cardElement) {
      setMsg("Card element not found");
      setLoading(false);
      return;
    }

    const { error, paymentIntent } = await stripe.confirmCardPayment(
      clientSecret,
      {
        payment_method: {
          card: cardElement,
          billing_details: {
            email: userDetails?.email,
            name: `${userDetails?.firstName} ${userDetails?.lastName}`.trim(),
            phone: userDetails?.mobile,
          },
        },
      },
    );

    if (error) {
      setMsg(error.message || "Payment failed");
      setLoading(false);
      return;
    }

    if (paymentIntent?.status !== "succeeded") {
      setMsg(`Payment status: ${paymentIntent?.status}`);
      setLoading(false);
      return;
    }

    // Payment successful - confirm with backend using job_id
    await confirmPayment(paymentIntent.id);
  };

  // Confirm payment with backend
  const confirmPayment = async (paymentIntentId) => {
    // Make sure we have the job_id
    if (!jobId) {
      setMsg("Missing job_id. Please try again.");
      setLoading(false);
      return;
    }

    const payload = {
      job_id: jobId, // Use job_id from create-order response
      payment_intent_id: paymentIntentId,
    };

    try {
      const apiUrl = apiEndpoints.confirmStripeEmailDownloadPayment;

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${JWT}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }).then((r) => r.json());

      if (response?.success) {
        setMsg("✅ Payment complete");

        if (onPaymentSuccess) {
          onPaymentSuccess(response.data);
        }

        setTimeout(() => {
          onClose();
          navigate("/my-downloads");
        }, 1000);
      } else {
        setMsg(response?.message || "Payment confirmation failed");
        setLoading(false);
      }
    } catch (error) {
      console.error("Error confirming payment:", error);
      setMsg("Failed to confirm payment with server");
      setLoading(false);
    }
  };

  const closeModal = () => {
    setMsg(null);
    setClientSecret(null);
    setJobId(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl bg-white rounded-xl shadow-2xl overflow-hidden">
        {/* Loader overlay */}
        {(loading || isCreatingOrder) && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white bg-opacity-75">
            <FiLoader className="animate-spin text-indigo-600 text-4xl" />
          </div>
        )}

        {/* Header */}
        <div className="border-b border-gray-200 bg-white">
          <div className="flex items-start justify-between py-4 px-6">
            <div className="text-xl font-bold text-gray-900">
              Complete Your Purchase
            </div>
            <button
              onClick={closeModal}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={loading || isCreatingOrder}
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          {/* Order Summary */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">
              Order Summary
            </h2>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Contacts to download:</span>
                <span className="font-medium text-gray-900">
                  {count.toLocaleString("en-US")} contacts
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Subtotal:</span>
                <span className="font-medium text-gray-900">
                  {formatCurrency(originalAmount)}
                </span>
              </div>

              {couponCode && originalAmount !== totalAmount && (
                <div className="flex justify-between items-center">
                  <span className="text-green-600">
                    Discount ({couponCode}):
                  </span>
                  <span className="text-green-600 font-medium">
                    -{formatCurrency(originalAmount - totalAmount)}
                  </span>
                </div>
              )}

              <div className="border-t border-gray-200 pt-3 mt-2">
                <div className="flex justify-between items-center">
                  <span className="text-gray-900 font-semibold">
                    Total Amount:
                  </span>
                  <span className="font-bold text-xl">
                    {formatCurrency(totalAmount)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* User Details Summary */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">
              Contact Details
            </h2>
            <div className="bg-blue-50 rounded-lg p-4 space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-gray-600 w-24">Name:</span>
                <span className="text-gray-900">
                  {userDetails?.firstName} {userDetails?.lastName}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-600 w-24">Email:</span>
                <span className="text-gray-900">
                  {userDetails?.email}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-600 w-24">Mobile:</span>
                <span className="text-gray-900">
                  {userDetails?.mobile}
                </span>
              </div>
            </div>
          </div>

          {/* Payment Form */}
          {clientSecret ? (
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">
                Card Details
              </h2>
              <div className="border rounded-md p-4 bg-white">
                <CardElement
                  options={{
                    style: {
                      base: {
                        fontSize: "16px",
                        color: "#424770",
                        "::placeholder": {
                          color: "#aab7c4",
                        },
                      },
                      invalid: {
                        color: "#9e2146",
                      },
                    },
                  }}
                />
              </div>
              {msg && (
                <p
                  className={`mt-2 text-sm ${msg.includes("✅") ? "text-green-500" : "text-red-500"
                    }`}
                >
                  {msg}
                </p>
              )}
            </div>
          ) : (
            !isCreatingOrder && (
              <div className="mb-6 text-center text-gray-500">
                {msg ? (
                  <p className="text-red-500">{msg}</p>
                ) : (
                  <p>Loading payment details...</p>
                )}
              </div>
            )
          )}

          {/* Security Info */}
          <div className="mb-6 space-y-2 text-xs sm:text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <LockIcon className="w-4 h-4 text-blue-600" />
              <span>256-bit SSL Encrypted Secure Checkout</span>
            </div>
            <div className="flex items-center gap-2">
              <LockIcon className="w-4 h-4 text-blue-600" />
              <span>PCI-DSS Compliant Payment Gateway</span>
            </div>
            <div className="flex items-center gap-2">
              <LockIcon className="w-4 h-4 text-blue-600" />
              <span>Safe & Trusted Transactions</span>
            </div>
          </div>

          {/* Payment Button */}
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={closeModal}
              disabled={loading || isCreatingOrder}
              className="px-6 min-w-[100px] h-10"
            >
              Cancel
            </Button>

            <Button
              onClick={handlePayment}
              disabled={
                loading ||
                isCreatingOrder ||
                !stripe ||
                !clientSecret ||
                !elements
              }
              className="px-6 min-w-[100px] h-10 bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              {loading ? "Processing..." : `Pay ${formatCurrency(totalAmount)}`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Wrapper component with Elements provider
export default function StripePopup({
  isOpen,
  onClose,
  batchId,
  count,
  totalAmount,
  originalAmount,
  couponCode = null,
  userDetails,
  onPaymentSuccess,
  remainingSessionId,
  remainingDownload,
}) {
  return (
    <Elements stripe={stripePromise}>
      <StripeCheckout
        isOpen={isOpen}
        onClose={onClose}
        batchId={batchId}
        count={count}
        totalAmount={totalAmount}
        originalAmount={originalAmount}
        couponCode={couponCode}
        userDetails={userDetails}
        onPaymentSuccess={onPaymentSuccess}
        remainingSessionId={remainingSessionId}
        remainingDownload={remainingDownload}
      />
    </Elements>
  );
}
